// Geometry helpers shared between the LeadEditPanel right panel and
// the LeadSearchPage left table. Moved out of LeadEditPanel.tsx so we
// don't break Vite fast-refresh by mixing components and utilities in
// the same module.

import { BuildingCandidate } from "@inrange/building-manager-api-client";

export const bboxesOverlap = (
  a: [number, number, number, number],
  b: [number, number, number, number]
): boolean => {
  // [west, south, east, north]. Overlap = neither rectangle is strictly
  // to the left/right/above/below the other.
  return a[0] <= b[2] && a[2] >= b[0] && a[1] <= b[3] && a[3] >= b[1];
};

// [west, south, east, north]. GeoJSON Polygon or MultiPolygon - we walk
// every ring vertex; cheap enough vs. the alternative dependency on
// turf.js or @mapbox/geojson-extent.
export const polygonBbox = (
  poly:
    | BuildingCandidate["polygon"]
    | number[][][]
    | number[][][][]
    | null
    | undefined
): [number, number, number, number] | null => {
  if (!poly) return null;
  const isMulti = Array.isArray((poly as number[][][][])?.[0]?.[0]?.[0]);
  const polys = isMulti
    ? (poly as number[][][][])
    : [poly as number[][][]];
  let west = Infinity;
  let south = Infinity;
  let east = -Infinity;
  let north = -Infinity;
  for (const rings of polys) {
    for (const ring of rings) {
      for (const pt of ring) {
        const lon = pt[0];
        const lat = pt[1];
        if (lon < west) west = lon;
        if (lon > east) east = lon;
        if (lat < south) south = lat;
        if (lat > north) north = lat;
      }
    }
  }
  if (!isFinite(west)) return null;
  return [west, south, east, north];
};

// Point-in-polygon (even-odd ray cast) on lon/lat coordinates. Good
// enough for axis-aligned property polygons; we don't need geodesic
// precision for "is the geocode pin inside this rooftop".
const isPointInRing = (lon: number, lat: number, ring: number[][]): boolean => {
  let inside = false;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const [xi, yi] = ring[i];
    const [xj, yj] = ring[j];
    const intersect =
      yi > lat !== yj > lat &&
      lon < ((xj - xi) * (lat - yi)) / (yj - yi + 1e-12) + xi;
    if (intersect) inside = !inside;
  }
  return inside;
};

// True when (lat, lon) falls inside any outer ring of the polygon /
// multipolygon.
export const polygonContainsPoint = (
  poly:
    | number[][][]
    | number[][][][]
    | null
    | undefined,
  lat: number,
  lon: number
): boolean => {
  if (!poly || poly.length === 0) return false;
  const isMulti = Array.isArray((poly as number[][][][])?.[0]?.[0]?.[0]);
  const polys = isMulti
    ? (poly as number[][][][])
    : [poly as number[][][]];
  for (const rings of polys) {
    if (!rings.length) continue;
    if (isPointInRing(lon, lat, rings[0])) return true;
  }
  return false;
};

// Approximate "polygons overlap" test for HMLR deed vs building
// polygon filtering. True when:
//   1. Polygon `a`'s centroid (if provided) is inside `b` (cheap path), OR
//   2. Any vertex of `a` is inside `b`, OR
//   3. Any vertex of `b` is inside `a`.
// (1) catches most cases; (2)+(3) catch L-shaped or notched deeds
// where the centroid lands in the L's notch, and the inverse case
// where a small deed sits inside a larger building footprint.
//
// Stops short of full edge-edge intersection (would need Sutherland-
// Hodgman or turf.js) - that catches the rare case where two convex
// polygons overlap with no vertex inside the other, but it's cheap
// to be slightly loose vs. cheap to be too tight: a false positive
// is "extra deed in the list" (operator scrolls past); a false
// negative is "real deed hidden" (operator picks worse fallback or
// ends up with empty options).
export const polygonsOverlap = (
  a: number[][][] | number[][][][] | null | undefined,
  b: number[][][] | number[][][][] | null | undefined,
  // Optional fast-path: centroid of polygon `a`. Tested against `b`
  // first because it's the most common "yes" path.
  aCentroid?: { lat: number; lon: number } | null
): boolean => {
  if (!a || !b) return false;
  if (aCentroid && polygonContainsPoint(b, aCentroid.lat, aCentroid.lon)) {
    return true;
  }
  const ringsA = ((Array.isArray((a as number[][][][])?.[0]?.[0]?.[0])
    ? (a as number[][][][])
    : [a as number[][][]])
  )
    .map((p) => p[0])
    .filter(Boolean);
  const ringsB = ((Array.isArray((b as number[][][][])?.[0]?.[0]?.[0])
    ? (b as number[][][][])
    : [b as number[][][]])
  )
    .map((p) => p[0])
    .filter(Boolean);
  for (const ringA of ringsA) {
    for (const [lon, lat] of ringA) {
      if (polygonContainsPoint(b, lat, lon)) return true;
    }
  }
  for (const ringB of ringsB) {
    for (const [lon, lat] of ringB) {
      if (polygonContainsPoint(a, lat, lon)) return true;
    }
  }
  return false;
};
